import { MILESTONES } from '../components/MilestoneModal';

// Catalog of unlockable achievements shown on the Library → Achievements
// screen. Unlock state is derived purely from data already in the client's
// auth state, so there is nothing extra to persist server-side: an
// achievement is unlocked the moment the relevant count crosses its threshold
// and stays unlocked.
//
// Count-based categories map to a stat we already track:
//   explored   — union of every artist the user has marked (header stat)
//   discovered — artists saved as discoveries
//   favorited  — artists favorited
//   friends    — accepted friend connections
//
// Genre categories are derived from the user's computed universe: each cluster
// carries the artists in it (`members`) and its dominant tags (`topTags`), so
// we can bucket members into a handful of major genres. See MAJOR_GENRES.
export const ACHIEVEMENT_CATEGORIES = {
  explored: { label: 'Explored', noun: 'artist', verb: 'Explore' },
  discovered: { label: 'Discovered', noun: 'discovery', verb: 'Discover' },
  favorited: { label: 'Favorited', noun: 'favorite', verb: 'Favorite' },
  // Combined: favorites + discoveries of artists with few Last.fm listeners.
  smallArtists: { label: 'Underground', noun: 'hidden gem', verb: 'Uncover' },
  friends: { label: 'Friends', noun: 'friend', verb: 'Connect with' },
};

// An artist counts as a "hidden gem" when it has a known Last.fm listener count
// below this. 0 / unknown is treated as "not counted", never as ultra-small —
// so a missing or failed listener fetch can't falsely unlock the achievement.
export const SMALL_ARTIST_MAX_LISTENERS = 100000;

// Thresholds per count-based category. Explored reuses the popup milestones so
// the two systems never disagree about what counts as a milestone.
const TIERS = {
  explored: MILESTONES, // [1, 10, 25, 50, 100, 250, 500, 1000]
  discovered: [1, 10, 25, 50, 100],
  favorited: [1, 10, 25, 50],
  smallArtists: [1, 5, 10, 25, 50],
  friends: [1, 5],
};

// Curated names for the marquee tiers. Anything without a curated name falls
// back to a generated "<n> <noun>s" title.
const TITLES = {
  'explored-1': 'First Light',
  'explored-10': 'Constellation',
  'explored-25': 'Stargazer',
  'explored-50': 'Voyager',
  'explored-100': 'Cartographer',
  'explored-250': 'Cosmic Explorer',
  'explored-500': 'Stellar Navigator',
  'explored-1000': 'Galaxy Master',
  'discovered-1': 'First Discovery',
  'discovered-10': 'Prospector',
  'discovered-25': 'Trailblazer',
  'discovered-50': 'Pathfinder',
  'discovered-100': 'Pioneer',
  'favorited-1': 'Smitten',
  'favorited-10': 'Curator',
  'favorited-25': 'Devotee',
  'favorited-50': 'Connoisseur',
  'smallArtists-1': 'Deep Cut',
  'smallArtists-5': 'Crate Digger',
  'smallArtists-10': 'Tastemaker',
  'smallArtists-25': 'Underground',
  'smallArtists-50': 'Kingmaker',
  'friends-1': 'Companion',
  'friends-5': 'Crew',
};

// A handful of major genres, each with the lowercase Last.fm tags we treat as
// belonging to it. A universe cluster is bucketed into the first genre (in the
// order below) whose synonym is a substring of one of the cluster's top tags,
// scanning the tags from most to least dominant — so the cluster's strongest
// tag decides its genre. Order matters: more specific genres come before the
// catch-all "rock" so e.g. "pop punk" lands in Pop and "metalcore" in Metal.
export const MAJOR_GENRES = [
  { key: 'hiphop', label: 'Hip-Hop', color: '#fbbf24', synonyms: ['hip hop', 'hip-hop', 'hiphop', 'rap', 'trap', 'grime', 'drill'] },
  { key: 'electronic', label: 'Electronic', color: '#22d3ee', synonyms: ['electronic', 'electronica', 'edm', 'house', 'techno', 'trance', 'dubstep', 'drum and bass', 'dnb', 'idm', 'ambient', 'synthwave', 'electro'] },
  { key: 'metal', label: 'Metal', color: '#94a3b8', synonyms: ['metal', 'metalcore', 'deathcore', 'thrash', 'doom', 'grindcore'] },
  { key: 'rnb', label: 'R&B & Soul', color: '#c084fc', synonyms: ['r&b', 'rnb', 'rhythm and blues', 'soul', 'funk', 'neo-soul', 'motown'] },
  { key: 'pop', label: 'Pop', color: '#f472b6', synonyms: ['pop', 'synthpop', 'electropop', 'dream pop', 'k-pop', 'kpop', 'dance-pop'] },
  { key: 'rock', label: 'Rock', color: '#f87171', synonyms: ['rock', 'indie', 'punk', 'grunge', 'alternative', 'alt-rock', 'garage', 'emo', 'shoegaze'] },
];

const GENRE_TIERS = [1, 5, 10];
const GENRE_TIER_TITLES = { 1: 'First Spin', 5: 'On Repeat', 10: 'Superfan' };

function noun(category, n) {
  const c = ACHIEVEMENT_CATEGORIES[category];
  if (n === 1) return c.noun;
  // "discovery" → "discoveries"
  if (c.noun.endsWith('y')) return c.noun.slice(0, -1) + 'ies';
  return c.noun + 's';
}

function describe(category, threshold) {
  // Underground needs the listener cutoff spelled out, and reads better as a
  // single line than the generic "<verb> <n> <noun>s" template.
  if (category === 'smallArtists') {
    const artists = threshold === 1 ? 'artist' : 'artists';
    return `Favorite or discover ${threshold.toLocaleString()} ${artists} with under 100K listeners`;
  }
  const c = ACHIEVEMENT_CATEGORIES[category];
  return `${c.verb} ${threshold.toLocaleString()} ${noun(category, threshold)}`;
}

// Static list of every count-based achievement, grouped by category and in
// ascending threshold order.
export const ACHIEVEMENT_GROUPS = Object.keys(ACHIEVEMENT_CATEGORIES).map((category) => ({
  category,
  label: ACHIEVEMENT_CATEGORIES[category].label,
  achievements: TIERS[category].map((threshold) => {
    const id = `${category}-${threshold}`;
    return {
      id,
      category,
      threshold,
      title: TITLES[id] || `${threshold.toLocaleString()} ${noun(category, threshold)}`,
      description: describe(category, threshold),
    };
  }),
}));

// One group per major genre, each with the 1 / 5 / 10 tiers.
export const GENRE_GROUPS = MAJOR_GENRES.map((g) => ({
  category: 'genre',
  genreKey: g.key,
  label: g.label,
  color: g.color,
  achievements: GENRE_TIERS.map((threshold) => ({
    id: `genre-${g.key}-${threshold}`,
    category: 'genre',
    genreKey: g.key,
    color: g.color,
    threshold,
    title: GENRE_TIER_TITLES[threshold],
    description: `Explore ${threshold} ${g.label} ${threshold === 1 ? 'artist' : 'artists'}`,
  })),
}));

// Bucket the artists in a computed universe into major-genre counts. Each
// cluster contributes its member count to exactly one genre (its dominant
// mappable tag), so a single eclectic cluster can't light up every genre.
export function genreCountsFromUniverse(universeData) {
  const counts = {};
  for (const g of MAJOR_GENRES) counts[g.key] = 0;
  const clusters = universeData?.clusters;
  if (!Array.isArray(clusters)) return counts;

  for (const cluster of clusters) {
    const memberCount = Array.isArray(cluster.members) ? cluster.members.length : 0;
    if (memberCount === 0) continue;
    const tags = (cluster.topTags && cluster.topTags.length ? cluster.topTags : [cluster.label])
      .filter(Boolean)
      .map((t) => String(t).toLowerCase());

    let assigned = null;
    for (const tag of tags) {
      for (const g of MAJOR_GENRES) {
        if (g.synonyms.some((s) => tag.includes(s))) { assigned = g.key; break; }
      }
      if (assigned) break;
    }
    if (assigned) counts[assigned] += memberCount;
  }
  return counts;
}

// Resolve every count the achievements screen needs from the auth state.
export function getAchievementStats({ favorites = [], dislikes = [], knownArtists = [], discoveredArtists = [], friends = [], universeData = null } = {}) {
  const explored = new Set();
  for (const f of favorites) explored.add(f.artistName);
  for (const d of dislikes) explored.add(d.artistName);
  for (const k of knownArtists) explored.add(k.artistName);
  for (const dd of discoveredArtists) explored.add(dd.artistName);

  // Underground: distinct artists across favorites + discoveries whose known
  // listener count is below the cutoff. Deduped by name so an artist that is
  // both favorited and discovered only counts once. Unknown counts (null/0,
  // e.g. not yet fetched) don't qualify.
  const smallArtists = new Set();
  for (const a of [...favorites, ...discoveredArtists]) {
    const n = a.listeners;
    if (typeof n === 'number' && n > 0 && n < SMALL_ARTIST_MAX_LISTENERS) {
      smallArtists.add(a.artistName);
    }
  }

  return {
    explored: explored.size,
    discovered: discoveredArtists.length,
    favorited: favorites.length,
    smallArtists: smallArtists.size,
    friends: friends.length,
    genres: genreCountsFromUniverse(universeData),
    universeReady: Array.isArray(universeData?.clusters) && universeData.clusters.length > 0,
  };
}

function withState(achievement, current, tally) {
  tally.total += 1;
  const unlocked = current >= achievement.threshold;
  if (unlocked) tally.unlockedCount += 1;
  return {
    ...achievement,
    current,
    unlocked,
    progress: Math.max(0, Math.min(1, current / achievement.threshold)),
  };
}

// Given the stats, return the catalog with per-achievement unlock state and
// progress toward the threshold, plus an overall unlocked/total tally.
export function buildAchievements(stats) {
  const tally = { unlockedCount: 0, total: 0 };

  const groups = ACHIEVEMENT_GROUPS.map((group) => {
    const current = stats[group.category] || 0;
    return {
      ...group,
      current,
      achievements: group.achievements.map((a) => withState(a, current, tally)),
    };
  });

  const genreGroups = GENRE_GROUPS.map((group) => {
    const current = (stats.genres && stats.genres[group.genreKey]) || 0;
    return {
      ...group,
      current,
      achievements: group.achievements.map((a) => withState(a, current, tally)),
    };
  });

  return {
    groups,
    genreGroups,
    genresPending: !stats.universeReady,
    unlockedCount: tally.unlockedCount,
    total: tally.total,
  };
}
